import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks';
import { GenerateFeedbackSummaryInput, Query } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface UseInitiateGenerateFeedbackSummaryOptions {
  onCompleted?: (responseID: string | undefined) => void;
}

interface InitiateGenerateFeedbackSummaryParams {
  sessionID: string;
  eql: string;
  shouldGenerateExecutiveSummary?: boolean;
  prompt?: string;
}

const GET_SEARCH_SUGGESTIONS_QUERY = gql`
  query generateFeedbackSummary(
    $tenantID: ID!
    $userID: ID!
    $sessionID: ID!
    $eql: String!
    $isExecutiveSummary: Boolean
    $shouldUseMarkdown: Boolean
    $prompt: String
    $shouldUseArtefactCitations: Boolean
  ) {
    generateFeedbackSummary(
      request: {
        tenantID: $tenantID
        userID: $userID
        sessionID: $sessionID
        eql: $eql
        isExecutiveSummary: $isExecutiveSummary
        shouldUseMarkdown: $shouldUseMarkdown
        prompt: $prompt
        shouldUseArtefactCitations: $shouldUseArtefactCitations
      }
    ) {
      responseID
    }
  }
`;

export function useInitiateGenerateFeedbackSummary(
  opts?: UseInitiateGenerateFeedbackSummaryOptions,
) {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();
  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const [generateFeedbackSummary, { loading, data, error }] = useLazyQuery<
    Query,
    GenerateFeedbackSummaryInput
  >(GET_SEARCH_SUGGESTIONS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to generate the feedback summary.',
        props: {},
      });
    },
    onCompleted: (data) => {
      opts?.onCompleted?.(data.generateFeedbackSummary?.responseID);
    },
  });

  const initiateGenerateFeedbackSummary = useCallback(
    ({ eql, sessionID, prompt }: InitiateGenerateFeedbackSummaryParams) => {
      generateFeedbackSummary({
        variables: {
          eql,
          sessionID,
          tenantID,
          userID,
          isExecutiveSummary: true,
          shouldUseMarkdown: true,
          prompt,
          shouldUseArtefactCitations: isChatWithFeedbackEnabled,
        },
      });
    },
    [tenantID, userID, generateFeedbackSummary, isChatWithFeedbackEnabled],
  );

  return [
    initiateGenerateFeedbackSummary,
    {
      isLoading: loading,
      responseID: data?.generateFeedbackSummary?.responseID,
      error,
    },
  ] as const;
}
